import { useEffect, useState } from 'react';
import '../types/chat';
import { getSelector } from '../selectors/registry';
import { mapDocumentToBrief } from '../utils/mapDocumentToBrief';
import { useBriefMutator } from './useBriefMutator';
import { useClaudeTurn } from './useClaudeTurn';
import { useMessageLog } from './useMessageLog';
import type { SelectorResult, SelectorType } from '../selectors/registry';
import type { PartialDesignBrief } from '../types/briefSchema';
import type { Message } from '../types/chat';
import type { UploadedAttachment } from '../types/selectors';

export interface UseChatOptions {
    designBrief: PartialDesignBrief;
    onBriefUpdate: (brief: PartialDesignBrief) => void;
    /**
     * Server-side chat history (including the initial agent greeting) used
     * to hydrate the conversation on mount. The seeded messages are rendered
     * with the typewriter animation skipped.
     */
    initialMessages?: Message[];
    /** Whether the brief is already complete on page load (from dynamicFields). */
    initialBriefComplete?: boolean;
    /** Fires the first time the user sends a message in this session. */
    onChatStarted?: () => void;
    initialMaxMessages?: number | null;
}

export interface UseChatReturn {
    messages: Message[];
    inputText: string;
    onInputChange: (text: string) => void;
    /** Successfully uploaded attachments staged for the next send. */
    attachments: UploadedAttachment[];
    /** Append one or more freshly uploaded attachments to the staging area. */
    onAttachmentsAdd: (added: UploadedAttachment[]) => void;
    /** Remove a single staged attachment by its MCP uploadId. */
    onAttachmentRemove: (uploadId: string) => void;
    isLoading: boolean;
    error: string | null;
    briefComplete: boolean;
    /** True once the user has sent at least one message in this session. */
    hasUserSent: boolean;
    messagesEndRef: React.Ref<HTMLDivElement>;
    onSend: () => Promise<void>;
    sendText: (content: string) => Promise<void>;
    /**
     * Unified entry point for completing any selector. The result type is
     * discriminated on `type` via the SelectorResultMap.
     */
    onSelectorComplete: <T extends SelectorType>(
        type: T,
        result: SelectorResult<T>
    ) => Promise<void>;
    retry: () => Promise<void>;
    retryCount: number;
    isStaleSession: boolean;
    isFatalError: boolean;
    maxMessages: number | null;
}

export function useChat({
    designBrief,
    onBriefUpdate,
    initialMessages,
    initialBriefComplete,
    onChatStarted,
    initialMaxMessages,
}: UseChatOptions): UseChatReturn {
    const [inputText, setInputText] = useState('');
    const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
    const [hasUserSent, setHasUserSent] = useState(false);

    const brief = useBriefMutator(designBrief, onBriefUpdate);
    const log = useMessageLog(initialMessages);
    const turn = useClaudeTurn({
        briefId: designBrief.briefId ?? '',
        append: log.append,
        initialBriefComplete,
        initialMaxMessages,
        initialMessages,
    });

    const briefComplete = turn.briefComplete;

    useEffect(() => {
        if (!turn.document) return;
        const patch = mapDocumentToBrief(turn.document);
        if (Object.keys(patch).length > 0) brief.update(patch);
    }, [turn.document, brief.update]);

    /** Append a user message and immediately ship the conversation to Claude. */
    const submitUserMessage = async (message: Message) => {
        log.append(message);
        await turn.runTurn(log.messages.concat(message));
    };

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const onSend = async () => {
        if ((!inputText.trim() && attachments.length === 0) || turn.isLoading) return;

        const currentAttachments = attachments;

        const userMessage: Message =
            currentAttachments.length > 0
                ? {
                      type: 'imageAttachment',
                      role: 'user',
                      images: currentAttachments,
                      caption: inputText || undefined,
                  }
                : {
                      type: 'text',
                      role: 'user',
                      content: inputText,
                  };

        log.append(userMessage);

        if (!hasUserSent) {
            setHasUserSent(true);
            onChatStarted?.();
        }
        setInputText('');
        setAttachments([]);

        if (currentAttachments.length > 0) {
            brief.update((prev) => ({
                referenceImages: [...(prev.referenceImages ?? []), ...currentAttachments],
            }));
        }

        // If there was a prior error, the failed user message is still at the end of
        // log.messages but the server never processed it. Strip it so expectedMessageCount
        // reflects what the server actually holds.
        const baseMessages = turn.error ? log.messages.slice(0, -1) : log.messages;

        try {
            await turn.runTurn([...baseMessages, userMessage]);
        } catch {
            // error is surfaced via turn.error state; message stays visible in UI
        }
    };

    const sendText = async (content: string) => {
        if (turn.isLoading) return;
        await submitUserMessage({
            type: 'text',
            role: 'user',
            content,
        });
    };

    /**
     * Dispatch completion of any selector through the registry. Applies the
     * optional brief patch, appends the message, then runs a Claude turn
     * unless the descriptor said to skip it (e.g. brief is complete).
     */
    const onSelectorComplete = async <T extends SelectorType>(
        type: T,
        result: SelectorResult<T>
    ) => {
        if (turn.isLoading) return;

        const descriptor = getSelector(type);
        const outcome = descriptor.completionFor(result, { designBrief });

        if (outcome.briefPatch) brief.update(outcome.briefPatch);
        log.append(outcome.message);

        if (outcome.skipTurn) return;
        await turn.runTurn(log.messages.concat(outcome.message));
    };

    return {
        messages: log.messages,
        inputText,
        onInputChange: setInputText,
        attachments,
        onAttachmentsAdd: (added) => setAttachments((prev) => [...prev, ...added]),
        onAttachmentRemove: (uploadId) =>
            setAttachments((prev) => prev.filter((a) => a.uploadId !== uploadId)),
        isLoading: turn.isLoading,
        error: turn.error,
        briefComplete,
        hasUserSent,
        messagesEndRef: log.endRef,
        onSend,
        sendText,
        onSelectorComplete,
        retry: turn.retry,
        retryCount: turn.retryCount,
        isStaleSession: turn.isStaleSession,
        isFatalError: turn.isFatalError,
        maxMessages: turn.maxMessages,
    };
}
